import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { UserProfile, User } from '../../../shared/models/auth.model';
import { RoutingSlipDetails } from '../../../shared/models/routing-slip.model';
import { RoutingSlipService } from '../../../shared/services/routing-slip.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-routing-slip-edit',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    ToastModule,
  ],
  providers: [RoutingSlipService, MessageService],
  templateUrl: './routing-slip-edit.component.html',
  styleUrl: './routing-slip-edit.component.scss',
})
export class RoutingSlipEditComponent implements OnInit {
  // Current user
  currentUser: User | null = null;

  // User profile data
  userProfile: UserProfile = {
    id: '',
    name: '',
    email: '',
    role: '',
    avatar: 'assets/images/logo.png',
  };

  // Form properties
  step1Form!: FormGroup;
  step2Form!: FormGroup;
  currentStep: number = 2; // Always show Step 2
  totalSteps: number = 2;

  // Step 2 form properties
  usersArray!: FormArray;

  // Options for dropdowns
  userOptions = [{ label: 'Select User', value: null }];

  permissionOptions: { label: string; value: any }[] = [
    // { label: 'Select Permission', value: null },
  ];

  // Loading state for permissions
  isLoadingPermissions: boolean = false;

  // Routing slip details
  routingSlipDetails: RoutingSlipDetails | null = null;
  loading: boolean = false;
  documentName: string = '';

  // Date properties
  minDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private routingSlipService: RoutingSlipService,
    private messageService: MessageService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.initializeForms();
    this.loadRoutingSlipDetails();
    this.loadPermissions();
  }

  /**
   * Format permission name to add proper spacing
   */
  private formatPermissionName(name: string): string {
    // Handle common permission name patterns
    if (name === 'ViewOnly') return 'View Only';
    if (name === 'Review') return 'Review';
    if (name === 'ApproveReject') return 'Approve/Reject';
    if (name === 'FullAccess') return 'Full Access';

    // Generic formatting for camelCase or PascalCase
    return name
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim(); // Remove leading space
  }

  /**
   * Load permissions from the API
   */
  private loadPermissions(): void {
    this.isLoadingPermissions = true;

    this.routingSlipService.getPermissions().subscribe({
      next: (permissions: any) => {
        // Transform the API response to match the expected format
        const transformedPermissions = permissions.map((permission: any) => ({
          label: this.formatPermissionName(permission.name),
          value: permission.id,
        }));

        // Update permissionOptions with API data
        this.permissionOptions = [
          // { label: 'Select Permission', value: null },
          ...transformedPermissions,
        ];

        this.isLoadingPermissions = false;
      },
      error: () => {
        this.isLoadingPermissions = false;

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load permissions. Please try again.',
          life: 5000,
        });
      },
    });
  }

  private loadRoutingSlipDetails(): void {
    this.loading = true;

    this.route.queryParams.subscribe((params) => {
      if (params['documentName']) {
        this.documentName = params['documentName'];
      }
    });

    const id = this.route.snapshot.paramMap.get('id') || '1';

    this.routingSlipService.getRoutingSlipById(id).subscribe({
      next: (details) => {
        this.routingSlipDetails = details;
        this.loading = false;
        this.populateForm();
      },
      error: () => {
        this.loading = false;
        // Set default values if loading fails
        this.populateFormWithDefaults();
      },
    });
  }

  private initializeForms(): void {
    // Step 1 Form - Document Details
    this.step1Form = this.fb.group({
      documentName: ['', [Validators.required, Validators.minLength(3)]],
      dueDate: ['', Validators.required],
    });

    // Step 2 Form - User Assignment with FormArray
    this.usersArray = this.fb.array([]);
    this.step2Form = this.fb.group({
      users: this.usersArray,
    });

    // Add initial user
    this.addUser();

    // Set default values immediately
    this.populateFormWithDefaults();
  }

  private populateForm(): void {
    if (this.routingSlipDetails) {
      // Parse the due date from the routing slip details
      const dueDate = this.routingSlipDetails.dueDate
        ? new Date(
            this.routingSlipDetails.dueDate.split('-').reverse().join('-')
          )
        : new Date();

      this.step1Form.patchValue({
        documentName:
          this.documentName ||
          this.routingSlipDetails.documentName ||
          'Document Name XYZ.PDF',
        dueDate: dueDate,
      });

      // Populate users if they exist
      if (
        this.routingSlipDetails.assignedUsers &&
        this.routingSlipDetails.assignedUsers.length > 0
      ) {
        this.usersArray.clear();
        this.routingSlipDetails.assignedUsers.forEach((user, index) => {
          this.addUser();
          const userGroup = this.usersArray.at(index);
          userGroup.patchValue({
            userId: user.id || (index + 1).toString(),
            permission: this.getDefaultPermission(index),
          });
        });
      } else {
        // If no assigned users, use default values as shown in the image
        this.populateFormWithDefaults();
      }
    }
  }

  private populateFormWithDefaults(): void {
    // Set default values when loading fails
    this.step1Form.patchValue({
      documentName: this.documentName || 'Document Name XYZ.PDF',
      dueDate: new Date('2025-07-12'),
    });

    // Set default users as shown in the image
    this.usersArray.clear();

    // Add first user - Jonathan Doe with full permissions
    this.addUser();
    this.usersArray.at(0).patchValue({
      userId: 'jonathan-doe',
      permission: 'full',
    });

    // Add second user - Charles Ben with approve/reject permission
    this.addUser();
    this.usersArray.at(1).patchValue({
      userId: 'charles-ben',
      permission: 'approve',
    });

    // Add third user - Selina Brown with no permission selected yet
    this.addUser();
    this.usersArray.at(2).patchValue({
      userId: 'selina-brown',
      permission: '',
    });
  }

  private getDefaultPermission(userIndex: number): string {
    if (userIndex === 0) {
      return 'full'; // First user gets full access
    } else if (userIndex === 1) {
      return 'approve'; // Second user gets approve/reject
    } else {
      return 'view'; // Other users get view only
    }
  }

  // Navigation methods
  cancel(): void {
    this.router.navigate(['/routing-slip']);
  }

  // Form submission
  async submitForm(): Promise<void> {
    if (this.step2Form.valid) {
      this.loading = true;

      try {
        const formData = {
          ...this.step1Form.value,
          ...this.step2Form.value,
          date: new Date().toISOString().split('T')[0],
          assignedDate: new Date().toISOString().split('T')[0],
          reviewStatus: '---',
          approvalStatus: 'Pending',
        };

        // Here you would typically make an API call to update the routing slip
        // await this.routingSlipService.updateRoutingSlip(this.routingSlipDetails?.id, formData).toPromise();

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Routing slip updated successfully',
        });

        // Navigate back to routing slip list
        setTimeout(() => {
          this.router.navigate(['/routing-slip']);
        }, 1500);
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update routing slip',
        });
      } finally {
        this.loading = false;
      }
    } else {
      this.markFormGroupTouched(this.step2Form);
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
      });
    }
  }

  // Helper method to mark all form controls as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // FormArray methods
  addUser(): void {
    const userGroup = this.fb.group({
      userId: ['', Validators.required],
      permission: ['', Validators.required],
    });

    this.usersArray.push(userGroup);
  }

  removeUser(index: number): void {
    if (this.usersArray.length > 1) {
      this.usersArray.removeAt(index);
    }
  }

  // Getter methods for form validation
  get isStep1Valid(): boolean {
    return this.step1Form.valid;
  }

  get isStep2Valid(): boolean {
    return this.step2Form.valid;
  }

  get canProceedToStep2(): boolean {
    return true; // Always true since we're always on Step 2
  }

  get canSubmit(): boolean {
    // Check if Step 2 form is valid
    if (!this.step2Form.valid) return false;
    
    // Check if document name and due date are filled
    const documentName = this.step1Form.get('documentName')?.value;
    const dueDate = this.step1Form.get('dueDate')?.value;
    
    return !!(documentName && dueDate && documentName.trim() !== '' && dueDate);
  }

  get usersFormArray(): FormArray {
    return this.step2Form.get('users') as FormArray;
  }
}
